import type * as Domain from "../../domain";
import type { GridSnapshot, GridStore } from "../types";

/**
 * Like CursorStore, but the innermost level is an Array instead of a Map.
 * Structure: Map<ProductId, Map<Field, (number | null | undefined)[]>>
 *
 * - Offset -> direct index into the array
 * - Undefined = "not set"
 */
export class ArrayCursorStore implements GridStore {
  private root = new Map<Domain.ProductId, Map<Domain.Field, (number | null | undefined)[]>>();

  // keep calculated as Maps – it's not in the hot path in your benchmark
  private calculated = new Map<Domain.ProductId, Map<Domain.Field, Map<Domain.Offset | string, Domain.CalcResult>>>();

  applyUpdate(event: Domain.GridUpdateEvent): void {
    if (event.type === "grid:update:root") {
      const { update, remove } = event.data;

      // Remove whole products
      if (remove.length > 0) {
        for (let i = 0; i < remove.length; i++) {
          const p = remove[i];
          this.root.delete(p);
          this.calculated.delete(p);
        }
      }

      // Cursor cache for product/field
      let lastProdId: Domain.ProductId | undefined;
      let lastProdMap: Map<Domain.Field, (number | null | undefined)[]> | undefined;
      let lastField: Domain.Field | undefined;
      let lastFieldArray: (number | null | undefined)[] | undefined;

      for (let i = 0; i < update.length; i++) {
        const u = update[i];

        // Level 1: product
        if (u.product !== lastProdId) {
          lastProdId = u.product;
          lastProdMap = this.root.get(u.product);
          if (!lastProdMap) {
            lastProdMap = new Map();
            this.root.set(u.product, lastProdMap);
          }
          lastField = undefined;
          lastFieldArray = undefined;
        }

        // Level 2: field
        if (u.field !== lastField) {
          lastField = u.field;
          lastFieldArray = lastProdMap!.get(u.field);
          if (!lastFieldArray) {
            lastFieldArray = [];
            lastProdMap!.set(u.field, lastFieldArray);
          }
        }

        // Level 3: offset -> array index
        const offset = u.offset as number;
        if (offset >= lastFieldArray!.length) {
          lastFieldArray!.length = offset + 1; // fast grow, leaves holes as undefined
        }
        lastFieldArray![offset] = u.result;
      }
    } else if (event.type === "grid:update:calculated") {
      const data = event.data;

      let lastProdId: Domain.ProductId | undefined;
      let lastProdMap: Map<Domain.Field, Map<Domain.Offset | string, Domain.CalcResult>> | undefined;
      let lastField: Domain.Field | undefined;
      let lastFieldMap: Map<Domain.Offset | string, Domain.CalcResult> | undefined;

      for (let i = 0; i < data.length; i++) {
        const u = data[i];

        if (u.product !== lastProdId) {
          lastProdId = u.product;
          lastProdMap = this.calculated.get(u.product);
          if (!lastProdMap) {
            lastProdMap = new Map();
            this.calculated.set(u.product, lastProdMap);
          }
          lastField = undefined;
          lastFieldMap = undefined;
        }

        if (u.field !== lastField) {
          lastField = u.field;
          lastFieldMap = lastProdMap!.get(u.field);
          if (!lastFieldMap) {
            lastFieldMap = new Map();
            lastProdMap!.set(u.field, lastFieldMap);
          }
        }

        lastFieldMap!.set(u.row, u.result);
      }
    }
  }

  readCell(product: Domain.ProductId, field: Domain.Field, row: Domain.Offset | string): Domain.CalcResult {
    // 1) Try calculated
    const cProd = this.calculated.get(product);
    if (cProd) {
      const cField = cProd.get(field);
      if (cField) {
        const calcVal = cField.get(row);
        if (calcVal) return calcVal;
      }
    }

    // 2) Fallback to root for numeric row
    if (typeof row === "number") {
      const rProd = this.root.get(product);
      if (rProd) {
        const rField = rProd.get(field);
        if (rField) {
          const v = rField[row];
          if (v !== undefined) {
            return { Ok: v as number, Err: undefined };
          }
        }
      }
    }

    return { Err: "No value found", Ok: undefined };
  }

  getSnapshot(): GridSnapshot {
    const root: Domain.RootProduct[] = [];
    const calculated: Domain.CalculatedProduct[] = [];

    for (const [product, fieldMap] of this.root) {
      for (const [field, arr] of fieldMap) {
        const len = arr.length;
        for (let offset = 0; offset < len; offset++) {
          const val = arr[offset];
          if (val !== undefined) {
            root.push({
              product,
              field,
              offset: offset as Domain.Offset,
              result: val,
            });
          }
        }
      }
    }

    for (const [product, fieldMap] of this.calculated) {
      for (const [field, rowMap] of fieldMap) {
        for (const [row, result] of rowMap) {
          calculated.push({ product, field, row, result });
        }
      }
    }

    return { root, calculated };
  }

  restoreSnapshot(snapshot: GridSnapshot): void {
    this.root.clear();
    this.calculated.clear();

    // For root we can reuse the fast path
    this.applyUpdate({ type: "grid:update:root", data: { update: snapshot.root, remove: [] } });
    this.applyUpdate({ type: "grid:update:calculated", data: snapshot.calculated });
  }

  clear(): void {
    this.root.clear();
    this.calculated.clear();
  }
}
